import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { MagnifyingGlassIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import CustomCards from "../CustomCards";
import CustomCardsSkeleton from "../CustomCardsSkeleton";
import Pagination from "../CustomPagination";
import Drawer from "../Drawer";
import ArchiveStatusFilter from "../Filters/ArchiveStatusFilter";
import MainSearchBar from "../MainSearchbar";
import Notification from "../Notification";
import {
  useGetAllDocumentsList,
  useGetAllDocumentsRequestList,
  useGetAllDocumentsRequestListAdmin,
  useGetDocumentDetails,
} from "../../custom-hooks/queries";
import { setDrawer } from "../../redux-store/features/layoutSlice";
import {
  isBackendViewOnlyUser,
  isDiligentAdminUserRole,
  useUserRole,
} from "../../utils/helperMethods";
import {
  mapSponsorOrgRequestToCardProps,
  mapVendorOrgLibraryItemToCardProps,
} from "../../utils/orgLibraryDocumentCardMapper";

/**
 * Library Documents tab on Manage Sponsor / Vendor Org Settings (US 14042 / US 14670).
 */
export default function OrgLibraryDocumentsTab({
  orgType,
  orgId,
  isCatalogAdmin: isCatalogAdminProp,
}) {
  const isSponsorOrg = orgType === "sponsor";
  const location = useLocation();
  const userRole = useUserRole();
  const isProviderRole =
    userRole === "Provider User" || userRole === "Provider Master User";
  const isVendorPortalSelfService = location.pathname.startsWith(
    "/user/team/profile/edit"
  );
  const isCatalogAdmin =
    isCatalogAdminProp ?? isDiligentAdminUserRole(userRole);
  /** Provider portal users cannot call POST /Documents (403); use Requests org_library_view. */
  const useVendorOrgLibraryRequestsApi =
    !isSponsorOrg && (isProviderRole || isVendorPortalSelfService);
  const useVendorOrgLibraryDocumentsApi =
    !isSponsorOrg &&
    !useVendorOrgLibraryRequestsApi &&
    Boolean(orgId) &&
    isCatalogAdmin;
  const hideRequestActions = isBackendViewOnlyUser(userRole);
  const dispatch = useDispatch();
  const isDrawerOpen = useSelector((state) => state.layout.drawerOpen);

  const pageKey = isSponsorOrg
    ? "orgSponsorLibraryDocuments"
    : "orgVendorLibraryDocuments";

  const searchQuery =
    useSelector(
      (state) => state.providersFilters.searchTextByPage[pageKey]
    ) || "";

  const [currentPage, setCurrentPage] = useState(1);
  const [archiveStatusFilter, setArchiveStatusFilter] = useState("active_only");
  const [docId, setDocId] = useState("");
  const [selectedDocumentItem, setSelectedDocumentItem] = useState(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, archiveStatusFilter, orgId]);

  const sponsorIdList = orgId ? [orgId] : [];

  const sponsorAdminQuery = useGetAllDocumentsRequestListAdmin(
    currentPage,
    [],
    [],
    [],
    [],
    [],
    sponsorIdList,
    searchQuery,
    {},
    0,
    isCatalogAdmin ? archiveStatusFilter : null,
    true,
    isSponsorOrg && isCatalogAdmin && Boolean(orgId)
  );

  const sponsorSelfQuery = useGetAllDocumentsRequestList(
    currentPage,
    [],
    [],
    [],
    [],
    [],
    searchQuery,
    [],
    [],
    {},
    0,
    true,
    isSponsorOrg && !isCatalogAdmin
  );

  const vendorAdminQuery = useGetAllDocumentsList(
    currentPage,
    searchQuery,
    [],
    orgId ? [orgId] : [],
    [],
    [],
    undefined,
    undefined,
    [],
    null,
    useVendorOrgLibraryDocumentsApi ? archiveStatusFilter : null,
    {},
    0,
    useVendorOrgLibraryDocumentsApi,
    true
  );

  const vendorSelfQuery = useGetAllDocumentsRequestList(
    currentPage,
    [],
    [],
    [],
    [],
    [],
    searchQuery,
    [],
    [],
    {},
    0,
    true,
    useVendorOrgLibraryRequestsApi
  );

  const activeQuery = isSponsorOrg
    ? isCatalogAdmin
      ? sponsorAdminQuery
      : sponsorSelfQuery
    : useVendorOrgLibraryDocumentsApi
    ? vendorAdminQuery
    : vendorSelfQuery;

  const { data, isLoading, isError, error, refetch: listRefetch } =
    activeQuery;

  const isVendorProviderSelfView = useVendorOrgLibraryRequestsApi;

  const items = isSponsorOrg
    ? isCatalogAdmin
      ? data?.items ?? []
      : data?.document_request_for_sponsor?.items ?? []
    : useVendorOrgLibraryDocumentsApi
    ? data?.items ?? []
    : data?.document_request_for_provider?.items ?? [];

  const totalPages = isSponsorOrg
    ? isCatalogAdmin
      ? data?.total_pages ?? 1
      : data?.document_request_for_sponsor?.total_pages ?? 1
    : useVendorOrgLibraryDocumentsApi
    ? data?.total_pages ?? 1
    : data?.document_request_for_provider?.total_pages ?? 1;

  const {
    data: drawerData,
    isPending: drawerLoading,
    refetch: drawerRefetch,
  } = useGetDocumentDetails(docId);

  const archiveDocumentContext = isSponsorOrg ? "sponsorOrg" : "vendorOrg";

  const openDrawer = (item, cardProps) => {
    setDocId(item?.document_id ?? cardProps?.docId);
    setSelectedDocumentItem(cardProps?.details ?? item);
    dispatch(setDrawer(true));
  };

  useEffect(() => {
    setShowError(isError);
  }, [isError]);

  return (
    <div className="w-full bg-white shadow-sm rounded-lg p-4 sm:p-6">
      {isSponsorOrg && isCatalogAdmin ? (
        <div className="mb-4 flex gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <InformationCircleIcon className="h-5 w-5 shrink-0 text-slate-500" />
          <p>
            <strong>Archive Document</strong> retires the doc from the Library for
            future requesters; this sponsor keeps access. To revoke this
            sponsor&apos;s access, use <strong>Archive Request</strong> on their
            request grid.
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <MainSearchBar
          pageKey={pageKey}
          placeholder="Search documents..."
          height="40px"
          width="20rem"
          isSearchIcon={true}
        />
        {isCatalogAdmin ? (
          <ArchiveStatusFilter
            value={archiveStatusFilter}
            onChange={setArchiveStatusFilter}
            className="shrink-0"
          />
        ) : null}
      </div>

      {isLoading ? (
        <CustomCardsSkeleton isProviderDetail={true} />
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center space-y-2 min-h-64 py-12">
          <span className="p-3 rounded-full bg-custom-link-bg-color">
            <MagnifyingGlassIcon width={20} height={20} className="text-white" />
          </span>
          <p className="text-lg text-gray-700 font-semibold">No documents found</p>
          <p className="text-gray-500 text-sm max-w-md">
            {isSponsorOrg
              ? "This sponsor has no Library-traceable documents yet."
              : "This vendor has no Library documents yet."}
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap justify-start gap-4">
          {items.map((item) => {
            const cardProps = isSponsorOrg
              ? mapSponsorOrgRequestToCardProps(item, isCatalogAdmin)
              : mapVendorOrgLibraryItemToCardProps(
                  item,
                  isVendorProviderSelfView
                );

            return (
              <CustomCards
                key={item?.document_id ?? item?.request_id}
                {...cardProps}
                hideRequestActions={hideRequestActions}
                libraryRefetch={listRefetch}
                archiveDocumentContext={archiveDocumentContext}
                onClick={() => openDrawer(item, cardProps)}
              />
            );
          })}
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <div className="mt-4">
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {isDrawerOpen && (
        <Drawer
          open={isDrawerOpen}
          docId={drawerData?.document_id ?? docId}
          docType={drawerData?.document_type ?? selectedDocumentItem?.entity_type}
          docName={drawerData?.document_name ?? selectedDocumentItem?.document_name}
          documents={drawerData}
          fallbackDocument={selectedDocumentItem}
          drawerLoading={drawerLoading}
          refetch={drawerRefetch}
          libraryRefetech={listRefetch}
        />
      )}

      <Notification
        show={showError}
        setShow={setShowError}
        errorMessage={error?.message}
        status="error"
      />
    </div>
  );
}
